const HEADERS = {
  "Content-Type": "application/json",
  "x-api-key": process.env.NEXT_PUBLIC_AMBEE_API_KEY,
};

// Utility function to capitalize the first character of a word
export function capitalizeFirstLetter(string) {
  if(string){
    return string
      .split(" ")
      .map((word) =>
        word.toLowerCase() === "and"
          ? word
          : word[0]?.length && word[0].toUpperCase() + word.slice(1)
      )
      .join(" ");
  }
  return string
}

export async function fetchStates(country, dataset) {
  try {
    const res = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/states?countryCode=${country}&dataset=${dataset}`,
      {
        next: { revalidate: 1800 },
        method: "GET",
        headers: HEADERS,
      }
    );
    const data = await res.json();
    if (res.status !== 200 || data.message !== "success") {
      return []; // Return an empty array if there's an issue
    }
    return data.data; // Return the states array
  } catch (error) {
    console.log("Error fetching states:", error);
    return []; // Return an empty array on error
  }
}

export async function fetchCitiesByState(state, dataset, page, city, country) {
  const countryCode = country == "us" || country === "USA" ? "USA" : "GBR";
  const stateName = capitalizeFirstLetter(state.replaceAll("-", " "));
  try {
    const res = await fetch(
      `${
        process.env.NEXT_PUBLIC_BACKEND_URL
      }/cities/by-state?state=${stateName}&dataset=${dataset}&page=${page}&countryCode=${countryCode}&city=${
        city || ""
      }`,
      {
        next: { revalidate: 1800 },
        method: "GET",
        headers: HEADERS,
      }
    );
    const data = await res.json();
    if (res.status !== 200 || data.message !== "success") {
      return []; // Return an empty array if there's an issue
    }
    return data;
  } catch (error) {
    console.log("Error fetching cities:", error);
    return []; // Return an empty array on error
  }
}

export async function fetchNearestStates(state, country, dataset) {
  const countryCode = country == "us" ? "USA" : "GBR";
  const stateName = capitalizeFirstLetter(state.replaceAll("-", " "));
  try {
    const res = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/nearest_state?countryCode=${countryCode}&state=${stateName}&dataset=${dataset}`,
      {
        next: { revalidate: 1800 },
        method: "GET",
        headers: HEADERS,
      }
    );
    const data1 = await res.json();
    if (res.status !== 200 || data1.message !== "success") {
      return []; // Return an empty array if there's an issue
    }
    return data1.data;
  } catch (error) {
    console.log("Error fetching nearby states:", error);
    return []; // Return an empty array on error
  }
}

export async function fetchNearestCities(city, state, dataset, country) {
  const countryCode = country == "us" ? "USA" : "GBR";
  const stateName = capitalizeFirstLetter(state.replaceAll("-", " "));
  const cityName = capitalizeFirstLetter(city.replaceAll("-", " "));
  try {
    const res = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/nearest_city?state=${stateName}&city=${cityName}&dataset=${dataset}&countryCode=${countryCode}`,
      {
        next: { revalidate: 1800 },
        method: "GET",
        headers: HEADERS,
      }
    );
    const data1 = await res.json();
    if (res.status !== 200 || data1.message !== "success") {
      return []; // Return an empty array if there's an issue
    }
    return data1.data;
  } catch (error) {
    console.log("Error fetching nearby cities:", error);
    return []; // Return an empty array on error
  }
}

export async function fetchFaqsByState(state, country, category) {
  const countryCode = country == "us" ? "USA" : "GBR";
  const stateName = capitalizeFirstLetter(state.replaceAll("-", " "));

  try {
    // Fetch FAQs from the custom API
    const faqResponse = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/faqs/by-state?state=${stateName}&dataset=${category}&countryCode=${countryCode}`,
      {
        next: { revalidate: 1800 },
        method: "GET",
        headers: HEADERS,
      }
    );

    const faqData = await faqResponse.json();

    if (faqResponse.status !== 200 || faqData.message !== "success") {
      console.error("Failed to fetch FAQs:", faqData.message);
      return []; // Return an empty array if there's an issue
    }

    return faqData.data; // Return the FAQ data
  } catch (error) {
    console.error("Error fetching FAQs:", error);
    return []; // Return an empty array on error
  }
}

export async function fetchFaqsByCity(city, state, category="aq", country) {
  const stateName = capitalizeFirstLetter(state.replaceAll("-", " "));
  const cityName = capitalizeFirstLetter(city.replaceAll("-", " "));
  const countryCode = country == "us" ? "USA" : "GBR";
  try {
    // Fetch FAQs from the custom API
    const faqResponse = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/faqs/by-city?state=${stateName}&city=${cityName}&dataset=${category}&countryCode=${countryCode}`,
      {
        next: { revalidate: 1800 },
        method: "GET",
        headers: HEADERS,
      }
    );

    const faqData = await faqResponse.json();

    if (faqResponse.status !== 200 || faqData.message !== "success") {
      console.error("Failed to fetch FAQs city:", faqData.message);
      return []; // Return an empty array if there's an issue
    }

    return faqData.data; // Return the FAQ data
  } catch (error) {
    console.error("Error fetching FAQs city:", error);
    return []; // Return an empty array on error
  }
}

export async function fetchHistoricalData(city, state, country, category) {
  const countryCode = country == "us" ? "USA" : "GBR";
  const stateName = capitalizeFirstLetter(state.replaceAll("-", " "));
  const cityName = capitalizeFirstLetter(city.replaceAll("-", " "));
  try {
    // Calculate the `to` and `from` dates based on the current time and the `hours` parameter
    const now = new Date();
    const to = new Date(now.getTime() + 60 * 60 * 1000); // Reduce the `to` date by 1 hour
    const from = new Date(now.getTime() - 47 * 60 * 60 * 1000); // Reduce the `from` date by 1 hour

    // Set minutes and seconds to 00
    to.setMinutes(0);
    to.setSeconds(0);
    from.setMinutes(0);
    from.setSeconds(0);

    // Format the `to` and `from` dates to YYYY-MM-DD HH:MM:SS
    const formattedTo = to.toISOString().replace("T", " ").split(".")[0]; // Convert to YYYY-MM-DD HH:MM:SS
    const formattedFrom = from.toISOString().replace("T", " ").split(".")[0]; // Convert to YYYY-MM-DD HH:MM:SS

    // Manually create the URL with exact format
    const url = city.length
      ? `${process.env.NEXT_PUBLIC_BACKEND_URL}/${category}/history/by-city?city=${cityName}&state=${stateName}&from=${formattedFrom}&to=${formattedTo}&locale=true`
      : `${process.env.NEXT_PUBLIC_BACKEND_URL}/${category}/history/by-state?countryCode=${countryCode}&state=${stateName}&from=${formattedFrom}&to=${formattedTo}&locale=true`;

    const res = await fetch(url, {
      next: { revalidate: 3600 }, // for history and forecast
      method: "GET",
      headers: HEADERS,
    });

    const data = await res.json();
    if (res.status !== 200 || data.message !== "success") {
      return []; // Return an empty array if there's an issue
    }
    // Process the response to return the expected format
    const result = data.data;
    return result;
  } catch (error) {
    console.log("Error fetching aqi data:", error);
    return []; // Return an empty array on error
  }
}

export async function fetchForecastData(city, state, country, category) {
  const countryCode = country == "us" ? "USA" : "GBR";
  const stateName = capitalizeFirstLetter(state.replaceAll("-", " "));
  const cityName = capitalizeFirstLetter(city.replaceAll("-", " "));
  try {
    // Construct the URL with the appropriate query parameter for hours
    const url = city.length
      ? `${process.env.NEXT_PUBLIC_BACKEND_URL}/${category}/forecast/by-city?state=${stateName}&city=${cityName}&countryCode=${countryCode}&locale=true`
      : `${process.env.NEXT_PUBLIC_BACKEND_URL}/${category}/forecast/by-state?state=${stateName}&countryCode=${countryCode}&locale=true`;

    // Fetch data from the API
    const res = await fetch(url, {
      next: { revalidate: 3600 }, // for history and forecast
      method: "GET",
      headers: HEADERS,
    });

    // Parse the JSON response
    const data = await res.json();
    // Check if the response was successful
    if (res.status !== 200 || data.message !== "success") {
      console.error("API request failed:", data.message);
      return []; // Return an empty array if there's an issue
    }
    const result = data.data.slice(0, 48);
    return result;
  } catch (error) {
    console.error("Error fetching aqi data:", error);
    return []; // Return an empty array on error
  }
}

export async function fetchHorizonData(city, state, country, category) {
  try {
    const res = await fetch(
      `/api/horizon?state=${state}&category=${category}&city=${city}&country=${country}`,
      {
        method: "GET",
      }
    );
    const data = await res.json();
    if (res.ok && data) {
      return data;
    } else {
      return []; // Return empty array if there's an issue
    }
  } catch (error) {
    console.error("Error fetching Horizon data:", error);
    return []; // Return empty array on error
  }
}

export async function fetchLatestState(state, country, category) {
  const countryCode = country == "us" ? "USA" : "GBR";
  const stateName = capitalizeFirstLetter(state.replaceAll("-", " "));
  try {
    const res = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/${category}/latest/by-state?countryCode=${countryCode}&state=${stateName}`,
      {
        cache: 'no-store', // force fresh fetch always
        method: "GET",
        headers: HEADERS,
      }
    );

    const data = await res.json();
    if (res.status !== 200 || data.message !== "success") {
      return []; // Return an empty array if there's an issue
    }
    // Process the response to return the expected format
    const result = data.data[0];
    return result;
  } catch (error) {
    console.log("Error fetching aqi data:", error);
    return []; // Return an empty array on error
  }
}

export async function fetchLatestCity(city, state, category, country) {
  const countryCode = country == "us" ? "USA" : "GBR";
  const stateName = capitalizeFirstLetter(state.replaceAll("-", " "));
  const cityName = capitalizeFirstLetter(city.replaceAll("-", " "));
  try {
    const res = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/${category}/latest/by-city?state=${stateName}&city=${cityName}&countryCode=${countryCode}`,
      {
        cache: 'no-store', // force fresh fetch always
        method: "GET",
        headers: HEADERS,
      }
    );

    const data = await res.json();
    if (res.status !== 200 || data.message !== "success") {
      return []; // Return an empty array if there's an issue
    }
    // Process the response to return the expected format
    const result = data.data[0];
    return result;
  } catch (error) {
    console.log("Error fetching aqi data:", error);
    return []; // Return an empty array on error
  }
}

export async function fetchCities(state, type, page, searchText, countryCode) {
  try {
    const res = await fetch(
      `/api/cities?state=${state}&dataset=${type}&page=${page}&country=${countryCode}&city=${searchText}`,
      {
        method: "GET",
      }
    );
    const data = await res.json();
    if (res.ok && data && data.message == "success") {
      return data;
    } else {
      return []; // Return empty array if there's an issue
    }
  } catch (error) {
    console.error("Error fetching aqi cities data by page:", error);
    return []; // Return empty array on error
  }
}

export async function fetchPollenAnalyticsByPage(
  category,
  type,
  page = 1,
  country
) {
  const countryCode = country == "us" ? "USA" : "GBR";
  try {
    const res = await fetch(
      `/api/analytics?sort=${type}&page=${page}&countryCode=${countryCode}&category=${category}`,
      {
        method: "GET",
      }
    );
    const data = await res.json();
    if (res.ok && data) {
      return data;
    } else {
      return []; // Return empty array if there's an issue
    }
  } catch (error) {
    console.error("Error fetching aqi analytics by page:", error);
    return []; // Return empty array on error
  }
}


export async function fetchAnalyticsData(category,sort,page,country) {
  const countryCode = country == "us" ? "USA" : "GBR";
  try {
    const res = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/${category}/analytics?sort=${sort}&page=${page}&countryCode=${countryCode}`,
      {
        cache: 'no-store', // force fresh fetch always
        method: "GET",
        headers: HEADERS,
      }
    );

    const data = await res.json();
    if (res.status !== 200 || data.message !== "success") {
      return []; // Return an empty array if there's an issue
    }
    // Process the response to return the expected format
    const result = data.data;
    return result;
  } catch (error) {
    console.log("Error fetching analytics data:", error);
    return []; // Return an empty array on error
  }
}